import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getRelativeFontSize,
  mediumFont,
  regularFont,
} from "../../../../utils/styles";

const PLANET_ORDER = [
  "Sun",
  "Moon",
  "Mars",
  "Mercury",
  "Jupiter",
  "Venus",
  "Saturn",
];

type PlanetData = {
  chesta_bala?: number;
  dig_bala?: number;
  drig_bala?: number;
  naisargika_bala?: number;
  total_virupas?: number;
  total_rupas?: number;
  required_rupas?: number;
  sthana_bala?: {
    uccha?: number;
    saptavargaja?: number;
    ojha?: number;
    kendradi?: number;
    drekkana?: number;
    total?: number;
  };
  kala_bala?: {
    dig?: number;
    nathonnatha?: number;
    paksha?: number;
    tribhaga?: number;
    vara?: number;
    hora?: number;
    masa?: number;
    abda?: number;
    ayana?: number;
    total?: number;
  };
};

function formatValue(v: number | undefined): string {
  if (v == null || Number.isNaN(v)) return "—";
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(2);
}

interface RowDef {
  label: string;
  labelKey?: string;
  getValue: (d: PlanetData) => number | undefined;
  bold?: boolean;
}

function buildRowDefs(): RowDef[] {
  return [
    // Sthana Bala
    {
      label: "Uccha",
      labelKey: "uccha",
      getValue: (d) => d.sthana_bala?.uccha,
    },
    {
      label: "Saptavargaja",
      labelKey: "saptavargaja",
      getValue: (d) => d.sthana_bala?.saptavargaja,
    },
    { label: "Ojha", labelKey: "ojha", getValue: (d) => d.sthana_bala?.ojha },
    {
      label: "Kendradi",
      labelKey: "kendradi",
      getValue: (d) => d.sthana_bala?.kendradi,
    },
    {
      label: "Drekkana",
      labelKey: "drekkana",
      getValue: (d) => d.sthana_bala?.drekkana,
    },
    {
      label: "Sthana",
      labelKey: "sthanaTotal",
      getValue: (d) => d.sthana_bala?.total,
    },
    // Kala Bala
    { label: "Dig", labelKey: "dig", getValue: (d) => d.dig_bala },
    {
      label: "Nathonnatha",
      labelKey: "nathonnatha",
      getValue: (d) => d.kala_bala?.nathonnatha,
    },
    {
      label: "Paksha",
      labelKey: "paksha",
      getValue: (d) => d.kala_bala?.paksha,
    },
    {
      label: "Tribhaga",
      labelKey: "tribhaga",
      getValue: (d) => d.kala_bala?.tribhaga,
    },
    { label: "Vara", labelKey: "vara", getValue: (d) => d.kala_bala?.vara },
    { label: "Hora", labelKey: "hora", getValue: (d) => d.kala_bala?.hora },
    { label: "Masa", labelKey: "masa", getValue: (d) => d.kala_bala?.masa },
    { label: "Abda", labelKey: "abda", getValue: (d) => d.kala_bala?.abda },
    { label: "Ayana", labelKey: "ayana", getValue: (d) => d.kala_bala?.ayana },
    {
      label: "Kaala",
      labelKey: "kalaTotal",
      getValue: (d) => d.kala_bala?.total,
    },
    { label: "Chesta", labelKey: "chesta", getValue: (d) => d.chesta_bala },
    {
      label: "Naisargika",
      labelKey: "naisargika",
      getValue: (d) => d.naisargika_bala,
    },
    { label: "Drig", labelKey: "drig", getValue: (d) => d.drig_bala },
    { label: "Shabdala", labelKey: "totalV", getValue: (d) => d.total_virupas },
    {
      label: "Shadbala (Rupas)",
      labelKey: "totalR",
      getValue: (d) => d.total_rupas,
    },
  ];
}

const tableStyles = {
  table: {
    borderCollapse: "collapse" as const,
    width: "100%",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "8px",
    overflow: "hidden",
  },
  headerCell: {
    ...mediumFont,
    fontSize: getRelativeFontSize(2),
    color: "#FFFFFF",
    backgroundColor: "rgba(144, 83, 246, 0.4)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    padding: "10px 12px",
    textAlign: "center" as const,
  },
  rowLabelCell: {
    ...regularFont,
    fontSize: getRelativeFontSize(1),
    color: "#FFFFFF",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    padding: "8px 12px",
    backgroundColor: "rgba(255, 255, 255, 0.06)",
  },
  dataCell: {
    ...regularFont,
    fontSize: getRelativeFontSize(1),
    color: "#FFFFFF",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    padding: "8px 12px",
    textAlign: "center" as const,
    backgroundColor: "rgba(255, 255, 255, 0.04)",
  },
  dataCellBold: {
    ...mediumFont,
    fontSize: getRelativeFontSize(1),
    color: "#FFFFFF",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    padding: "8px 12px",
    textAlign: "center" as const,
    backgroundColor: "rgba(255, 255, 255, 0.08)",
  },
};

interface CustomProps {
  shadbalaDetails: Record<string, PlanetData> | PlanetData[] | undefined;
}

const ShadbalaTable = (props: CustomProps) => {
  const { t } = useTranslation();

  if (!props.shadbalaDetails) return null;

  const rawDetails: Record<string, PlanetData> = Array.isArray(
    props.shadbalaDetails,
  )
    ? (props.shadbalaDetails as PlanetData[]).reduce(
        (acc, item) => {
          const name = (item as any).planet;
          if (name) acc[name] = item;
          return acc;
        },
        {} as Record<string, PlanetData>,
      )
    : (props.shadbalaDetails as Record<string, PlanetData>);

  const findKey = (p: string) =>
    Object.keys(rawDetails).find((k) => k.toLowerCase() === p.toLowerCase());
  const detailsByPlanet: Record<string, PlanetData> = {};
  PLANET_ORDER.forEach((p) => {
    const key = findKey(p);
    if (key) detailsByPlanet[p] = rawDetails[key];
  });
  const planets = PLANET_ORDER.filter((p) => detailsByPlanet[p] != null);
  if (planets.length === 0) return null;

  const rowDefs = buildRowDefs();

  return (
    <Box sx={{ overflowX: "auto", py: 2 }}>
      <Table sx={{ ...tableStyles.table, minWidth: 640 }} size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={tableStyles.headerCell}>
              {t("bala") !== "bala" ? t("bala") : "Bala"}
            </TableCell>
            {planets.map((planet) => (
              <TableCell key={planet} sx={tableStyles.headerCell}>
                <strong>{planet}</strong>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rowDefs.map((row) => (
            <TableRow key={row.label}>
              <TableCell
                sx={{
                  ...tableStyles.rowLabelCell,
                  ...(row.bold ? tableStyles.dataCellBold : {}),
                }}
              >
                {row.bold ? <strong>{row.label}</strong> : row.label}
              </TableCell>
              {planets.map((planet) => {
                const value = row.getValue(detailsByPlanet[planet]);
                const formatted = formatValue(value);
                return (
                  <TableCell
                    key={planet}
                    sx={
                      row.bold ? tableStyles.dataCellBold : tableStyles.dataCell
                    }
                  >
                    {row.bold ? <strong>{formatted}</strong> : formatted}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ShadbalaTable;
